import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { ORIGIN } from '../api.constant';

@Injectable({ providedIn: 'root' })
export class MicrosoftAuthService {
  private readonly tenantId = environment.microsoft.tenantId;
  private readonly clientId = environment.microsoft.clientId;
  private readonly redirectUri = environment.microsoft.redirectUri;
  private readonly scope = 'openid profile email';

  // 1. Initiate Login
  async login() {
    // A. Generate the Secret Verifier
    const codeVerifier = this.generateCodeVerifier();

    // B. Hash it to create the Challenge
    const codeChallenge = await this.generateCodeChallenge(codeVerifier);

    // C. Save Verifier temporarily (we need it after the redirect!)
    sessionStorage.setItem('pkce_verifier', codeVerifier);

    // D. Construct the Azure URL
    const authUrl =
      `https://login.microsoftonline.com/${this.tenantId}/oauth2/v2.0/authorize` +
      `?client_id=${this.clientId}` +
      `&response_type=code` +
      `&redirect_uri=${encodeURIComponent(this.redirectUri)}` +
      `&response_mode=query` +
      `&scope=${encodeURIComponent(this.scope)}` +
      `&code_challenge=${codeChallenge}` +
      `&code_challenge_method=S256`;

    // E. Redirect the user
    this.redirect(authUrl);
  }

  redirect(url: string): void {
    window.location.href = url;
  }

  // 2. Handle the Return Trip
  getLoginParamsFromUrl(urlParams: URLSearchParams) {
    const code = urlParams.get('code');
    const verifier = sessionStorage.getItem('pkce_verifier');

    if (code && verifier) {
      // Clean up storage
      sessionStorage.removeItem('pkce_verifier');

      // Return the exact payload your backend expects
      return {
        code: code,
        codeVerifier: verifier,
        redirectUri: this.redirectUri,
        origin: ORIGIN,
      };
    }
    return null;
  }

  generateCodeVerifier(): string {
    const array = new Uint8Array(64);

    window.crypto.getRandomValues(array);

    return this.base64UrlEncode(array);
  }

  // 2. Generate the Challenge (Async)

  async generateCodeChallenge(verifier: string): Promise<string> {
    const encoder = new TextEncoder();

    const data = encoder.encode(verifier);

    // SHA-256 Hash

    const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);

    // Convert to Base64URL

    return this.base64UrlEncode(new Uint8Array(hashBuffer));
  }

  // 3. Helper: Base64URL Encoding (Standard Base64 -> URL Safe)

  private base64UrlEncode(array: Uint8Array): string {
    let str = '';
    for (const value of array) {
      str += String.fromCharCode(value);
    }

    let base64 = btoa(str).replace(/\+/g, '-').replace(/\//g, '_');

    // Remove padding safely
    while (base64.endsWith('=')) {
      base64 = base64.slice(0, -1);
    }

    return base64;
  }
}
